<template>
  <div class="patients">
    <v-overlay :value="loading" z-index="4">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <div v-if="isAllowedToChangePrograms" class="programs_header">
      <div class="programs_filter">
        <v-select
          v-model="programs"
          :items="availablePrograms"
          :item-text="'name'"
          return-object
          chips
          label="Filter by programs"
          clearable
          multiple
          @input="updateQueryParams"
          placeholder="Select program"
        >
          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index === 0">
              <span>{{ item.name }}</span>
            </v-chip>
            <span v-if="index === 1" class="grey--text text-caption">
              (+{{ programs.length - 1 }} others)
            </span>
          </template>
        </v-select>
      </div>
      <div class="programs_assigment">
        <v-btn
          text
          small
          v-if="
            practicesSelectedEditableList.length > 0 || practicesSelectedAll
          "
          class="routed-pill mr-2"
          @click="changeSelectAll(false)"
        >
          Unselect All
        </v-btn>
        <v-checkbox
          label="Select all"
          color="accent"
          :hide-details="true"
          @change="changeSelectAll($event)"
          v-model="practicesSelectedAll"
          off-icon="bhi-ellipse"
          on-icon="bhi-check-circle-filled"
        />
        <v-btn
          class="rounded-pill elevation-0 ml-2"
          small
          :disabled="
            practicesSelectedEditableList.length < 1 && !practicesSelectedAll
          "
          color="accent"
          @click="openAssignToSelection"
          >Assign program</v-btn
        >
      </div>
    </div>

    <main-user-table
      :headers="headers"
      :items="practicePatientsComputed"
      :items-per-page="queryParams.pageSize"
      :server-items-length="pagination.totalCount || -1"
      :page="queryParams.page"
      :row-component="
        isAllowedToChangePrograms
          ? 'practice-patients-table-row-with-select'
          : 'practice-patients-table-row'
      "
      :users-all-selected="practicesSelectedAll"
      @on-select="onSelect"
      @to-user="toPatient"
      @action:get-data="getDataAction"
    />
    <v-btn
      v-if="floatingButton && !isConsultant"
      :disabled="!isPracticeActive"
      color="primary"
      fixed
      bottom
      right
      fab
      large
      elevation="0"
      @click="addNewPracticePatient"
    >
      <v-icon size="24">mdi-plus</v-icon>
    </v-btn>
    <new-practice-patient-dialog
      :show-dialog="showNewPracticePatientDialog"
      @close-dialog="closeNewPracticePatientDialog"
      :practice-id="practiceId"
      :practice-user-id="practiceUserId"
      :journal-available="practice.journalAvailable"
      :smart-survey-available="practice.smartSurveyAvailable"
      :survey-available="practice.surveyAvailable"
      :swap-available="practice.swapAvailable"
      @on-submit="onNewPatientSubmit"
    />
    <program-assign-dialog
      v-if="isAllowedToChangePrograms"
      :show-dialog="showPracticeAssignDialog"
      @close-dialog="closePracticeAssignDialog"
      @on-submit="assignToSelection"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import NewPracticePatientDialog from '@/components/dialogs/NewPracticePatientDialog.vue'
import ProgramAssignDialog from '@/components/dialogs/ProgramAssignDialog.vue'
import RouterMixin from '@/mixins/router'
import MainUserTable from '@/components/tables/MainUserTable'

export default {
  name: 'PracticePatients',
  components: {
    MainUserTable,
    NewPracticePatientDialog,
    ProgramAssignDialog,
  },
  mixins: [RouterMixin],
  data() {
    return {
      floatingButton: true,
      headers: [
        { sortable: false },
        { text: 'Name', value: 'fullName' },
        { text: 'Join date', value: 'creationDate' },
        { text: 'Programs', value: 'programs', sortable: false },
        { text: 'Last updated swap', value: 'lastUpdatedSwaps' },
        { text: 'Most recent activity ', value: 'lastActivity' },
        { text: 'Phone no.', value: 'phone', sortable: false },
        { text: 'Notes', value: 'notes', sortable: false },
      ],
      showNewPracticePatientDialog: false,
      showPracticeAssignDialog: false,
      defaultQueryParams: {
        sortBy: 'fullName',
        sortDirection: 'asc',
      },
      defaultPatientData: {
        notes: '',
        steps: 0,
        waterGlasses: 0,
      },
      programs: [],
      practicesSelectedEditableList: [],
      practicesSelectedAll: false,
    }
  },
  computed: {
    ...mapState('practicesStates', [
      'practice',
      'practicePatients',
      'loading',
      'pagination',
    ]),
    ...mapState('programsStates', {
      availablePrograms: 'programs',
      availableProgramsLoading: 'loading',
    }),
    ...mapState('userStates', ['userId']),
    ...mapState('dictionariesStates', ['dictionaryPracticeUserRole']),
    ...mapGetters('userStates', ['getRolename']),
    practiceId() {
      return parseInt(this.$route.params.id)
    },
    practiceUserId() {
      if (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Advisor
      )
        return parseInt(this.userId)
      else return null
    },
    isPracticeAdmin() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Administrator
      )
    },
    isAdvisor() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Advisor
      )
    },
    isAllowedToChangePrograms() {
      return this.isPracticeAdmin || this.isAdvisor
    },
    isConsultant() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Consultant
      )
    },
    practicePatientsComputed() {
      let arr = []
      this.practicePatients.forEach(item => {
        let tmp = { ...item }
        tmp.selected = false
        if (this.practicesSelectedEditableList.indexOf(tmp.profileId) !== -1)
          tmp.selected = true
        arr.push(tmp)
      })
      return arr
    },
    isPracticeActive() {
      return this.practice.isActive
    },
  },
  mounted() {
    if (this.isAllowedToChangePrograms) {
      this.getPrograms({
        companyId: this.practiceId,
        active: true,
      })
    }
  },
  methods: {
    ...mapActions('programsStates', [
      'getPrograms',
      'assignProgramsToProfile',
      'assignProfilesToProgram',
      'unassignProfilesFromProgram',
      'assignProgramsToPracticeParticipants',
    ]),
    ...mapActions('practicesStates', [
      'getPracticePatients',
      'addPracticePatient',
    ]),
    toPatient(id) {
      this.$router.push({
        name: 'Patient',
        path: '/participants/:id',
        params: { id: id },
      })
    },
    getData(data) {
      this.getPracticePatients({ id: this.practiceId, params: data })
    },
    updateQueryParams(programs) {
      this.programs = programs
      this.queryParams.programsId = programs.map(el => el.id).join(',')
      this.getData({ ...this.queryParams, page: 0 })
    },
    addNewPracticePatient() {
      this.showNewPracticePatientDialog = true
    },
    closeNewPracticePatientDialog() {
      this.showNewPracticePatientDialog = false
    },
    closePracticeAssignDialog() {
      this.showPracticeAssignDialog = false
    },
    onNewPatientSubmit(patientData) {
      let patient = { ...patientData, ...this.defaultPatientData }
      this.addPracticePatient({ id: this.practiceId, data: patient })
    },
    resetList() {
      this.practicesSelectedEditableList = []
    },
    changeSelectAll(value) {
      this.practicesSelectedAll = value
      if (!value) {
        this.resetList()
      }
    },
    onSelect({ id, value }) {
      let i = this.practicesSelectedEditableList.indexOf(id)
      if (value == 0) {
        if (i !== -1) {
          this.practicesSelectedEditableList.splice(i, 1)
        }
      } else {
        if (i == -1) this.practicesSelectedEditableList.push(id)
      }
    },
    openAssignToSelection() {
      this.showPracticeAssignDialog = true
    },
    async assignToSelection(program) {
      try {
        if (this.practicesSelectedAll) {
          await this.assignProgramsToPracticeParticipants({
            practiceId: this.practiceId,
            programs: [program],
          })
        } else {
          const profileIds = this.practicesSelectedEditableList.map(
            profileId => ({
              profileId,
            }),
          )
          await this.assignProfilesToProgram({
            profiles: profileIds,
            id: program,
            companyId: this.practiceId,
          })
        }
        this.changeSelectAll(false)
        this.getData({
          ...this.queryParams,
          page: Math.max(parseInt(this.queryParams.page || '0', 10) - 1, 0),
        })
      } catch (error) {
        console.log(error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.patients {
  padding: 32px;
  &__tabs {
    flex: initial;
    width: auto;
    padding: 4px;
    border: 1px solid $bh-blue--4;

    &--wrap {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .v-tab {
      color: $bh-blue;
      font-weight: 700;

      &--active {
        background: $bh-blue;
        color: $bh-white !important;
      }
    }
  }

  &--wrap {
    display: flex;
  }

  &-sidebar {
    width: 272px;
    padding: 24px;

    textarea:not([readonly]) {
      padding: 4px;
    }
  }

  &-action-btn {
    cursor: pointer;
    font-weight: 600;
  }

  &-field {
    margin-bottom: $gutter;
  }

  label.bh-text-input__label {
    font-weight: 700;
    line-height: 1.425rem;
    color: $bh-gray--2;
    margin-bottom: 3px;
  }

  .programs_header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    .programs_filter {
      max-width: 250px;
      width: 100%;
      margin-bottom: 0;
    }
    .programs_assigment {
      display: flex;
      align-items: center;
      .v-input--selection-controls {
        margin-top: 0;
      }
    }
  }
}
</style>
